/**
 * @file Continuous cone weapons — the flamethrower (issue #14).
 *
 * A stream weapon spawns nothing. Firing 48 short-lived projectile entities a
 * second would cost more in allocation and collision than the whole rest of
 * combat, so the stream is a *sector sweep*: every tick the system finds what
 * stands inside the cone and hurts it directly. The flame the player sees is
 * particles, which are already instanced and free.
 *
 * Cadence comes from the weapon's own cooldown, exactly like a rifle's, so the
 * damage rate is `damage x pellets x fireRate` regardless of frame rate — a
 * per-frame sweep would make the weapon twice as strong on a 120 Hz screen.
 *
 * The system is owner-agnostic: it reads `Weapon` and `Transform`, so the
 * player and a burner enemy run the same code with different layer masks.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Transform,
  Weapon,
  Collider,
  Health,
  Burn,
  FireResult,
  statMultiplier,
  TimedStatus,
  timedStatusRemaining
} from '../components/index.js';
import { ParticleSystem } from './ParticleSystem.js';
import { Stat } from '../config/Powerups.js';
import { Layer } from '../config/Layers.js';
import { PALETTE } from '../config/Palette.js';
import { Events } from '../core/EventBus.js';
import { raycastAABB } from '../physics/AABB.js';
import { angleDelta } from '../utils/MathUtils.js';

/** Hard ceiling on how long a refreshed burn may last, in seconds. */
const MAX_BURN_DURATION = 5;
/** Stream ticks between camera shakes; at 24 Hz that is a rumble, not a quake. */
const SHAKE_EVERY_TICKS = 6;
/** Flame particles emitted per tick, before the pressure valve below. */
const FLAME_PARTICLES = 10;
/** Impact sparks per burning target, per tick. */
const IMPACT_PARTICLES = 3;
/** Live-particle counts at which flame emission is halved, then quartered. */
const PARTICLE_PRESSURE = [800, 1200];
/** Ticks a single frame may resolve, so one long frame cannot dump a burst. */
const MAX_TICKS_PER_FRAME = 2;

export class StreamFlameSystem extends System {
  static priority = 31;

  constructor() {
    super();
    /** @type {THREE.Vector3} @private */
    this._aim = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._hitPoint = new THREE.Vector3();
    /** @type {any[]} Scratch: targets inside the cone this tick. @private */
    this._targets = [];
    /** @type {any[]} Scratch: walls close enough to block this stream. @private */
    this._walls = [];
    /** @type {boolean} Whether a stream was burning last frame. @private */
    this._streaming = false;
    /** @type {ParticleSystem | undefined} Resolved lazily; see `_flameCount`. @private */
    this._particles = undefined;
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];

    /** @type {object} @private */
    this._fireOptions = { asDirection: true, rateMultiplier: 1, ammoCost: 1, unlimitedAmmo: false };
    /** @type {object} @private */
    this._damageEvent = { target: null, amount: 0, source: null, point: this._hitPoint };
    /** @type {object} @private */
    this._flashEvent = {
      position: this._hitPoint,
      color: PALETTE.flame,
      intensity: 4,
      life: 0.09
    };
    /** @type {object} @private */
    this._shakeEvent = { amount: 0 };
    /** @type {object} @private */
    this._firedEvent = { position: this._hitPoint, direction: this._aim, owner: null };
    /** @type {object} @private */
    this._streamEvent = { entity: null, weapon: null };
    /** @type {object} @private */
    this._burnEvent = { entity: null, burn: null };
    /** @type {object} Emission shape for the plume; mutated per tick. @private */
    this._plumeOptions = {
      speed: [0, 0],
      spread: 0.4,
      life: [0.2, 0.5],
      size: [0.15, 0.35],
      color: PALETTE.flame,
      yOffset: 0
    };
    /** @type {object} Emission shape for hit sparks. @private */
    this._sparkOptions = {
      speed: [2, 7],
      spread: 0.8,
      life: [0.15, 0.4],
      size: [0.1, 0.24],
      color: PALETTE.flameHot,
      yOffset: 0.5
    };
    /** @type {object} @private */
    this._burstEvent = { position: this._hitPoint, direction: this._aim, count: 0, options: null };
  }

  /** @returns {void} */
  init() {
    // Any state change stops the stream: Loading and Menu do not tick the world,
    // so without this a level transition would leave the fire loop roaring.
    this._subscriptions.push(this.ctx.events.on(Events.STATE_CHANGED, () => this._setStreaming(null)));
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    /** @type {import('../ecs/Entity.js').Entity | null} */
    let active = null;

    for (const entity of this.world.query(Weapon, Transform)) {
      const weapon = entity.get(Weapon);
      if (!weapon.streaming) continue;

      if (!weapon.triggerHeld) {
        // No state survives a released trigger, so re-pressing it never
        // resumes mid-tick with a stale accumulator.
        weapon.streamAccumulator = 0;
        continue;
      }
      if (this._fire(entity, weapon)) active = active ?? entity;
    }

    this._setStreaming(active);
  }

  /**
   * Resolves every stream tick the weapon's cooldown allows this frame.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Weapon holder.
   * @param {import('../components/Weapon.js').Weapon} weapon Weapon component.
   * @returns {boolean} True when at least one tick went out.
   * @private
   */
  _fire(entity, weapon) {
    // Factories call equip(); this is a belt-and-braces fallback.
    if (weapon.owner !== entity) weapon.equip(entity);

    this._fireOptions.rateMultiplier = statMultiplier(entity, Stat.FIRE_RATE);
    this._fireOptions.unlimitedAmmo = timedStatusRemaining(entity, TimedStatus.UNLIMITED_AMMO) > 0;
    // One tick = one shot for ammo. Pellet count multiplies damage in the cone
    // sweep, matching projectile weapons that spend a single round per volley.

    let fired = false;
    for (let tick = 0; tick < MAX_TICKS_PER_FRAME; tick++) {
      const transform = entity.get(Transform);
      this._aim.set(Math.sin(transform.rotationY), 0, Math.cos(transform.rotationY));
      if (weapon.tryFire(this._aim, this._fireOptions) !== FireResult.FIRED) break;

      if (!fired) {
        this._streamEvent.entity = entity;
        this._streamEvent.weapon = weapon;
      }
      fired = true;

      this._sweep(entity, weapon);
      this._emitFlame(entity, weapon);
    }
    return fired;
  }

  // -------------------------------------------------------------- the sweep

  /**
   * Hurts everything inside the cone. A target's own radius widens the cone at
   * point-blank range, which is what makes the flame feel like a blast of fire
   * rather than a narrow laser you have to aim.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Weapon holder.
   * @param {import('../components/Weapon.js').Weapon} weapon Weapon component.
   * @returns {void}
   * @private
   */
  _sweep(entity, weapon) {
    const origin = weapon.muzzle;
    const facing = Math.atan2(this._aim.x, this._aim.z);
    const rangeSq = weapon.range * weapon.range;

    this._targets.length = 0;
    for (const target of this.world.query(Health, Transform, Collider)) {
      if (target === entity || !target.alive) continue;

      const collider = target.get(Collider);
      if ((weapon.projectileMask & collider.layer) === 0) continue;

      const position = target.get(Transform).position;
      const dx = position.x - origin.x;
      const dz = position.z - origin.z;
      const distanceSq = dx * dx + dz * dz;
      if (distanceSq > rangeSq) continue;

      const distance = Math.sqrt(distanceSq);
      // A body directly on the muzzle has no meaningful bearing: it is in.
      if (distance > 1e-4) {
        const reach = Math.max(collider.halfX, collider.halfZ) + weapon.projectileRadius;
        const halfAngle = weapon.spread + Math.atan2(reach, distance);
        if (Math.abs(angleDelta(facing, Math.atan2(dx, dz))) > halfAngle) continue;
      }

      this._targets.push(target);
    }

    if (this._targets.length === 0) return;
    this._collectWalls(origin, weapon.range);

    const damage = weapon.damage * weapon.pellets * statMultiplier(entity, Stat.DAMAGE);
    // Snapshot first: applying damage can destroy entities and rebuild queries
    // underneath an in-flight iteration.
    for (const target of this._targets) {
      if (!target.alive) continue;
      const position = target.get(Transform).position;
      if (this._blocked(origin, position)) continue;

      this._hitPoint.set(position.x, weapon.muzzleHeight, position.z);
      this._damageEvent.target = target;
      this._damageEvent.amount = damage;
      this._damageEvent.source = entity;
      this.ctx.events.emit(Events.DAMAGE, this._damageEvent);

      this._applyBurn(target, weapon);
      this._burst(this._hitPoint, IMPACT_PARTICLES, this._sparkOptions);
    }
  }

  /**
   * Caches the static walls near the muzzle, so the line-of-sight test below
   * runs against a handful of boxes instead of the whole level.
   *
   * @param {THREE.Vector3} origin Muzzle position.
   * @param {number} range Weapon range.
   * @returns {void}
   * @private
   */
  _collectWalls(origin, range) {
    this._walls.length = 0;
    for (const entity of this.world.query(Collider, Transform)) {
      const collider = entity.get(Collider);
      if (!collider.isStatic || (collider.layer & Layer.WALL) === 0) continue;

      const position = entity.get(Transform).position;
      const reach = range + Math.max(collider.halfX, collider.halfZ);
      const dx = position.x - origin.x;
      const dz = position.z - origin.z;
      if (dx * dx + dz * dz > reach * reach) continue;

      this._walls.push(entity);
    }
  }

  /**
   * Line of sight from the muzzle to a target centre. Flames die on geometry:
   * an enemy behind a corner must be safe (issue #14 §Impacto).
   *
   * @param {THREE.Vector3} origin Muzzle position.
   * @param {THREE.Vector3} target Target position.
   * @returns {boolean} True when a wall stands in the way.
   * @private
   */
  _blocked(origin, target) {
    const dx = target.x - origin.x;
    const dz = target.z - origin.z;

    for (const entity of this._walls) {
      const collider = entity.get(Collider);
      const position = entity.get(Transform).position;
      const t = raycastAABB(
        origin.x,
        origin.z,
        dx,
        dz,
        position.x - collider.halfX,
        position.z - collider.halfZ,
        position.x + collider.halfX,
        position.z + collider.halfZ
      );
      // Anything short of the target blocks; a grazing hit at the far end is
      // the target's own cell and must not count.
      if (t >= 0 && t < 0.995) return true;
    }
    return false;
  }

  /**
   * Applies or refreshes a burn. Refreshing rather than stacking is what keeps
   * a held trigger from building an unbounded damage-over-time, and the hard
   * cap keeps the tail short after the player looks away.
   *
   * @param {import('../ecs/Entity.js').Entity} target Burning entity.
   * @param {import('../components/Weapon.js').Weapon} weapon Source weapon.
   * @returns {void}
   * @private
   */
  _applyBurn(target, weapon) {
    if (weapon.burnDamagePerSecond <= 0 || weapon.burnDurationSeconds <= 0) return;

    const existing = target.get(Burn);
    if (existing) {
      existing.damagePerSecond = Math.max(existing.damagePerSecond, weapon.burnDamagePerSecond);
      existing.duration = weapon.burnDurationSeconds;
      existing.remaining = Math.min(
        existing.remaining + weapon.burnDurationSeconds,
        MAX_BURN_DURATION
      );
      return;
    }

    const burn = target.add(
      new Burn(weapon.burnDamagePerSecond, weapon.burnDurationSeconds)
    );
    this._burnEvent.entity = target;
    this._burnEvent.burn = burn;
    this.ctx.events.emit(Events.BURN_APPLIED, this._burnEvent);
  }

  // ------------------------------------------------------------------ effects

  /**
   * The visible half of a tick: a plume of fire from the muzzle, a pulse of
   * orange light, and an occasional rumble.
   *
   * @param {import('../ecs/Entity.js').Entity} _entity Weapon holder.
   * @param {import('../components/Weapon.js').Weapon} weapon Weapon component.
   * @returns {void}
   * @private
   */
  _emitFlame(_entity, weapon) {
    const muzzle = weapon.muzzle;

    // Particles are ballistic, so their speed has to cover the range within
    // their lifetime or the plume stops short of what the cone actually hits.
    this._plumeOptions.speed[0] = weapon.range * 0.5;
    this._plumeOptions.speed[1] = weapon.range * 1.1;
    this._plumeOptions.spread = weapon.spread + 0.16;
    this._plumeOptions.yOffset = 0;
    // Alternating hue per tick reads as flicker without a second emitter.
    this._plumeOptions.color =
      weapon.streamAccumulator % 2 === 0 ? PALETTE.flame : PALETTE.flameHot;

    this._hitPoint.copy(muzzle);
    this._burst(muzzle, this._flameCount(), this._plumeOptions);

    this._flashEvent.color = this._plumeOptions.color;
    this.ctx.events.emit(Events.FLASH, this._flashEvent);

    weapon.streamAccumulator++;
    if (weapon.shake > 0 && weapon.streamAccumulator % SHAKE_EVERY_TICKS === 0) {
      this._shakeEvent.amount = weapon.shake;
      this.ctx.events.emit(Events.SHAKE, this._shakeEvent);
    }
  }

  /**
   * Flame particles for this tick, backed off as the shared pool fills so a
   * firefight full of explosions never starves the other effects.
   * @returns {number}
   * @private
   */
  _flameCount() {
    this._particles ??= this.world.getSystem(ParticleSystem);
    const live = this._particles?.liveCount ?? 0;
    if (live >= PARTICLE_PRESSURE[1]) return Math.round(FLAME_PARTICLES * 0.25);
    if (live >= PARTICLE_PRESSURE[0]) return Math.round(FLAME_PARTICLES * 0.5);
    return FLAME_PARTICLES;
  }

  /**
   * @param {THREE.Vector3} position Emission point.
   * @param {number} count Particles to emit.
   * @param {object} options Emission shape.
   * @returns {void}
   * @private
   */
  _burst(position, count, options) {
    if (count <= 0) return;
    this._burstEvent.position = position;
    this._burstEvent.count = count;
    this._burstEvent.options = options;
    this.ctx.events.emit(Events.FX_BURST, this._burstEvent);
  }

  /**
   * Raises or drops the "a flame is burning" edge that drives the looping fire
   * sound. Tracked per frame from live weapons rather than per weapon, so a
   * mid-stream weapon swap or a dead player can never strand the loop on.
   *
   * @param {import('../ecs/Entity.js').Entity | null} active A streaming holder, if any.
   * @returns {void}
   * @private
   */
  _setStreaming(active) {
    const streaming = Boolean(active);
    if (streaming === this._streaming) return;
    this._streaming = streaming;

    if (!streaming) {
      this._streamEvent.entity = null;
      this._streamEvent.weapon = null;
      this.ctx.events.emit(Events.FLAME_END, this._streamEvent);
      return;
    }

    this.ctx.events.emit(Events.FLAME_START, this._streamEvent);
    // One ignition report, not one per tick: 24 shot sounds a second is a
    // machine gun, and the fire loop covers everything after it.
    this._firedEvent.owner = active;
    this.ctx.events.emit(Events.WEAPON_FIRED, this._firedEvent);
  }

  /** @returns {void} */
  dispose() {
    this._setStreaming(null);
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    this._targets.length = 0;
    this._walls.length = 0;
    this._particles = undefined;
  }
}
